import axios from 'axios';

const INITIAL_STATE = {
  isLoading: false,
  showMobileOtp: false,
  showEmailOtp: false,
  isMobileNumberVerified: false,
  isEmailVerified: false,
  step: 1,
  mobileData: {
    countryCode: '+65',
    mobileNumber: '',
  },
  mobileOtp: '',
  newEmail: '',
  emailOtp: '',
  email: '',
  password: '',
  forgotPasswordOtp: '',
  forgotPasswordToken: null,
  forgotEmailToken: null,
  otpAttempts: 0,
};

const state = () => INITIAL_STATE;

const mutations = {
  setLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  setShowMobileOtp(state, showMobileOtp) {
    state.showMobileOtp = showMobileOtp;
  },
  setShowEmailOtp(state, showEmailOtp) {
    state.showEmailOtp = showEmailOtp;
  },
  setIsMobileNumberVerified(state, isMobileNumberVerified) {
    state.isMobileNumberVerified = isMobileNumberVerified;
  },
  setIsEmailVerified(state, isEmailVerified) {
    state.isEmailVerified = isEmailVerified;
  },
  setStep(state, step) {
    state.step = step;
  },
  setMobileData(state, mobileData) {
    state.mobileData = mobileData;
  },
  setMobileOtp(state, mobileOtp) {
    state.mobileOtp = mobileOtp;
  },
  setNewEmail(state, newEmail) {
    state.newEmail = newEmail;
  },
  setEmailOtp(state, emailOtp) {
    state.emailOtp = emailOtp;
  },
  setEmail(state, email) {
    state.email = email;
  },
  setPassword(state, password) {
    state.password = password;
  },
  setForgotPasswordOtp(state, otp) {
    state.forgotPasswordOtp = otp;
  },
  setForgotPasswordToken(state, token) {
    state.forgotPasswordToken = token;
  },
  setForgotEmailToken(state, token) {
    state.forgotEmailToken = token;
  },
  setOTPAttempts(state, attempt) {
    state.otpAttempts = attempt;
  },
};

const actions = {
  async sendMobileOtp({ commit, dispatch }, { countryCode, contactNumber }) {
    commit('setLoading', true);
    try {
      const { data, status } = await axios.post(
        process.env.VUE_APP_API_URL + '/v2/profile/forgot-email-send-mobile-otp',
        {
          countryCode,
          contactNumber,
        },
      );
      if (status !== 200) throw new Error();
      dispatch('toast/success', 'Mobile OTP sent successfully', { root: true });
      commit('setShowMobileOtp', true);
    } catch (e) {
      dispatch('toast/error', e.response.data.message, { root: true });
      throw e;
    } finally {
      commit('setLoading', false);
    }
  },
  async verifyMobileOtp({ commit, dispatch }, { countryCode, contactNumber, otp }) {
    commit('setLoading', true);
    try {
      const response = await axios.post(
        process.env.VUE_APP_API_URL + '/v2/profile/forgot-email-verify-mobile-otp',
        {
          contactNumber,
          countryCode,
          otp,
        },
        {
          withCredentials: true,
        },
      );
      commit('setForgotEmailToken', response.data.token);
      commit('setIsMobileNumberVerified', true);
    } catch (e) {
      // Only dispatch toast if it's not an incorrect OTP error
      if (!e.response?.data?.message?.includes('Incorrect OTP')) {
        dispatch('toast/error', e.response.data.message, { root: true });
      }
      throw e;
    } finally {
      commit('setLoading', false);
    }
  },
  async sendEmailOtp({ commit, dispatch }, email) {
    commit('setLoading', true);
    try {
      const { status } = await axios.post(
        process.env.VUE_APP_API_URL + '/v2/profile/forgot-email-send-email-otp',
        { email: email },
        {
          withCredentials: true,
        },
      );
      if (status !== 200) throw new Error('Failed in sending email otp');
      dispatch('toast/success', 'Email OTP sent successfully', { root: true });
      commit('setShowEmailOtp', true);
    } catch (e) {
      dispatch('toast/error', e.response.data.message, { root: true });
      throw e;
    } finally {
      commit('setLoading', false);
    }
  },
  async registerEmail({ commit, state }, { contactNumber, countryCode, email, otp }) {
    commit('setLoading', true);
    try {
      await axios.post(
        process.env.VUE_APP_API_URL + '/v2/profile/forgot-email',
        {
          contactNumber,
          countryCode,
          otp,
          email,
          token: state.forgotEmailToken,
        },
        {
          withCredentials: true,
        },
      );
      commit('setIsEmailVerified', true);
      // Reset OTP attempts on success
      commit('setOTPAttempts', 0);
    } catch (e) {
      //Increase OTP attempts on failure
      commit('setOTPAttempts', state.otpAttempts + 1);
      throw e;
    } finally {
      commit('setLoading', false);
    }
  },

  async sendForgotPasswordOtp({ commit, dispatch }, email) {
    commit('setLoading', true);
    try {
      const response = await axios.post(
        process.env.VUE_APP_API_URL + '/v2/profile/forgot-password-send-otp',
        { email },
        {
          withCredentials: true,
        },
      );
      if (response.status !== 201) throw new Error();
      commit('setEmail', email);
      dispatch('toast/success', 'OTP sent successfully to your email', { root: true });
    } catch (e) {
      dispatch('toast/error', e.response.data.message, { root: true });
      throw e;
    } finally {
      commit('setLoading', false);
    }
  },

  async verifyForgotPasswordOtp({ commit, dispatch }, { email, otp }) {
    commit('setLoading', true);
    try {
      const response = await axios.post(
        process.env.VUE_APP_API_URL + '/v2/profile/forgot-password-verify-otp',
        { email, otp },
        {
          withCredentials: true,
        },
      );
      if (response.status !== 201) throw new Error();
      commit('setForgotPasswordOtp', otp);
      commit('setForgotPasswordToken', response.data.token);
      dispatch('toast/show', 'OTP verified successfully', { root: true });
    } catch (e) {
      // Only dispatch toast if it's not an incorrect OTP error
      if (!e.response?.data?.message?.includes('Incorrect OTP')) {
        dispatch('toast/error', e.response.data.message, { root: true });
      }
      throw e;
    } finally {
      commit('setLoading', false);
    }
  },

  async resetPassword({ commit, dispatch, state }, { email, password, otp }) {
    commit('setLoading', true);
    try {
      const response = await axios.post(
        process.env.VUE_APP_API_URL + '/v2/profile/forgot-password',
        { email, newPassword: password, otp, token: state.forgotPasswordToken },
        {
          withCredentials: true,
        },
      );
      if (response.status !== 201) throw new Error('Password reset failed');
      dispatch('toast/success', 'Password reset successfully', { root: true });
    } catch (e) {
      dispatch('toast/error', e.response.data.message, { root: true });
      throw e;
    } finally {
      commit('setLoading', false);
    }
  },

  async submitEmailForm({ commit, dispatch }, forgotEmailForm) {
    commit('setLoading', true);
    try {
      //To-do: selectedOption should be included
      const { status } = await axios.post(
        process.env.VUE_APP_API_URL + '/v2/profile/forgot-email-form',
        {
          fullName: forgotEmailForm.fullName,
          cg: forgotEmailForm.cg,
          contactNumber: forgotEmailForm.contactNumber,
          countryCode: forgotEmailForm.countryCode,
          email: forgotEmailForm.email,
        },
      );
      if (status !== 200) throw new Error();
    } catch (e) {
      dispatch('toast/error', e.response.data.message, { root: true });
    } finally {
      commit('setLoading', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
